/**
 * activeModifiers.js — single resolver for the ephemeral state-pill list.
 *
 * Surfaces every active modifier that is NOT a persistent active_cards entry —
 * i.e. carry-over flags written into game.state by recently-played instant or
 * lock-in cards. Previously this list was assembled inline at the GameBoard
 * render site (~140 lines of conditional pushes); pulling it into a pure
 * resolver gives every consumer (GameBoard pill rail, active-manager-bar,
 * spectator overlays) the same normalized shape:
 *
 *   { key, cardId, label, sublabel, owner, description, isWindow?, isListener? }
 *
 *   key          — stable React key (unique across the returned list)
 *   cardId       — the source card id, for icons / lookups
 *   label        — primary headline ("Plunk Them +2")
 *   sublabel     — short qualifier ("this at-bat", "first swing", names…)
 *   owner        — "mine" | "opp" — relative to the caller's role
 *   description  — long-form text for hover/title attributes
 *   isWindow     — true for transient play-this-now opportunities (BOGO, etc.)
 *   isListener   — true for armed listener cards (auto-fire when conditions met)
 *
 * Pure function — no React, no async, no state. Identity is not preserved
 * across calls; callers that need referential stability should memoize.
 */

/**
 * @param {object|null} game        Full game document (game.state required).
 * @param {string|null} myRole      "home" / "away" / null. null = local play
 *                                  (shared screen) — every pill renders as "mine".
 * @param {Array<{id:string,name:string}>} allCards  Card catalogue for name lookup.
 * @returns {Array<object>}         Pill list, empty if no state.
 */
export function resolveActiveModifiers(game, myRole, allCards) {
  const s = game?.state;
  if (!s) return [];

  const pills = [];
  const battingRole = s.half === "bottom" ? "home" : "away";
  const pitchingRole = battingRole === "home" ? "away" : "home";
  // In local mode myRole is null — treat every pill as "mine" (shared screen, neutral style).
  const resolveOwner = (role) => !myRole ? "mine" : (role === myRole ? "mine" : "opp");

  const cardsById = {};
  (allCards || []).forEach(c => { cardsById[c.id] = c; });

  // pending_pitcher_modifier — Plunk Them: pitching team holds +N for this at-bat
  if (s.pending_pitcher_modifier?.amount) {
    pills.push({
      key: "pending_pitcher_mod",
      cardId: "plunk_them",
      label: `Plunk Them +${s.pending_pitcher_modifier.amount}`,
      sublabel: "this at-bat",
      owner: resolveOwner(pitchingRole),
      description: `Pitcher gets +${s.pending_pitcher_modifier.amount} for this at-bat (Plunk Them carry-over)`,
    });
  }

  // pending_first_swing_modifier — Tensions High: batting team gets +N on first swing
  if (s.pending_first_swing_modifier?.amount) {
    const fsTeam = s.pending_first_swing_modifier.team;
    pills.push({
      key: "pending_first_swing",
      cardId: "tensions_high",
      label: `Tensions High +${s.pending_first_swing_modifier.amount}`,
      sublabel: "first swing",
      owner: resolveOwner(fsTeam),
      description: `Batter gets +${s.pending_first_swing_modifier.amount} on their first swing (Tensions High carry-over)`,
    });
  }

  // home_lineup_protection / away_lineup_protection — Lineup Protection pins
  // specific batter IDs that get +1 only when they're up. Pill names the pinned
  // batters (or shows a count if names can't be resolved).
  for (const role of ["home", "away"]) {
    const lp = s[`${role}_lineup_protection`];
    const pinnedIds = lp?.player_ids;
    if (Array.isArray(pinnedIds) && pinnedIds.length > 0) {
      const teamKey = role === "home" ? "home_team" : "away_team";
      const players = (game?.[teamKey]?.players) || [];
      const names = pinnedIds
        .map(pid => players.find(p => p.id === pid)?.name)
        .filter(Boolean);
      const amount = lp.amount_per_batter || 1;
      pills.push({
        key: `lineup_protection_${role}`,
        cardId: "lineup_protection",
        label: `Lineup Protection +${amount}`,
        sublabel: names.length > 0 ? names.join(" + ") : `${pinnedIds.length} pinned`,
        owner: resolveOwner(role),
        description: names.length > 0
          ? `${names.join(" and ")} each get +${amount} on their swing rolls this inning (Lineup Protection)`
          : `Pinned batters get +${amount} on their swing rolls this inning (Lineup Protection)`,
      });
    }
  }

  // pending_at_bat_batter_modifier — Swing Away / Down Swinging / etc: modifier per roll for rest of at-bat
  if (s.pending_at_bat_batter_modifier?.amount) {
    const abm = s.pending_at_bat_batter_modifier;
    const abmCardId = abm.card_id || "swing_away";
    const abmCard = cardsById[abmCardId];
    const abmName = abmCard?.name || "Swing Away";
    pills.push({
      key: "at_bat_batter_mod",
      cardId: abmCardId,
      label: `${abmName} +${abm.amount}`,
      sublabel: "this at-bat",
      owner: resolveOwner(abm.team),
      description: `Batter gets +${abm.amount} on every roll for this at-bat (${abmName})`,
    });
  }

  // pending_bogo_walk — window is open for batting team to play BOGO Walk.
  // In online mode only show the pill if the current player actually holds the card;
  // showing it without the card in hand is misleading and leaks window timing to the opponent.
  if (s.pending_bogo_walk) {
    const bogoOwner = resolveOwner(battingRole);
    const bogoHand = bogoOwner === "mine" && myRole ? (game?.[`${myRole}_hand`] || []) : null;
    if (!bogoHand || bogoHand.includes("bogo_walk")) {
      pills.push({
        key: "bogo_walk",
        cardId: "bogo_walk",
        label: "BOGO Walk",
        sublabel: "available",
        owner: bogoOwner,
        description: "A walk just occurred — BOGO Walk can be played to send the next batter to first",
        isWindow: true,
      });
    }
  }

  // last_result_was_hbp — window is open for batting team to play Tensions High.
  // Same hand-check guard as BOGO Walk above.
  if (s.last_result_was_hbp) {
    const hbpOwner = resolveOwner(battingRole);
    const hbpHand = hbpOwner === "mine" && myRole ? (game?.[`${myRole}_hand`] || []) : null;
    if (!hbpHand || hbpHand.includes("tensions_high")) {
      pills.push({
        key: "hbp_window",
        cardId: "tensions_high",
        label: "HBP — Tensions High?",
        sublabel: "window open",
        owner: hbpOwner,
        description: "A Hit By Pitch just occurred — Tensions High can be played this at-bat",
        isWindow: true,
      });
    }
  }

  // active_listeners — listener cards currently armed (auto-fire when conditions met)
  const listeners = s.active_listeners || {};
  [["home", listeners.home], ["away", listeners.away]].forEach(([role, ids]) => {
    (ids || []).forEach((cardId) => {
      const lCard = cardsById[cardId];
      pills.push({
        key: `listener_${role}_${cardId}`,
        cardId,
        label: lCard ? lCard.name : cardId,
        sublabel: "armed",
        owner: resolveOwner(role),
        description: `${lCard?.name || cardId} will auto-fire when conditions are met`,
        isListener: true,
      });
    });
  });

  return pills;
}
